import Immutable from 'seamless-immutable';
import _ from 'lodash';
import { createReducer } from 'redux-act';

import RefData from '../../ref-data';

import IntegrationEnums from './integration-enums';
import IntegrationActions from './integration-actions';
import { prepareWizardFields } from './utils';

const initialWizardIntegrationState = Immutable({
  isActive:    false,
  template:    {},
  apiUrl:      undefined,
  campaignId:  undefined,
  steps:       [],
  fields:      {},
  mapping:     {},
  mappingData: {
    content:    [],
    isFetching: false,
  },
  dataSources:     {},
  allowNextStep:   false,
  initialAPISetup: true,
  isLoading:       false,
});

const initialState = Immutable({
  byId:                  {},
  allIds:                [],
  isFetching:            false,
  wizardIntegration:     initialWizardIntegrationState,
  credentialsById:       {},
  isFetchingCredentials: false,
});

export default createReducer((on) => {
  // ################################################################################################################
  // SETUP/TEARDOWN
  // ################################################################################################################
  on(IntegrationActions.reset, (state) => {
    return initialState;
  });

  // ################################################################################################################
  // INTEGRATIONS
  // ################################################################################################################

  on(IntegrationActions.fetchIntegrations, (state) => {
    return state.set('isFetching', true);
  });

  on(IntegrationActions.fetchIntegrationsFailure, (state) => {
    return state.set('isFetching', false);
  });

  on(IntegrationActions.fetchIntegrationsSuccess, (state, { integrations }) => {
    const byId = {};
    const allIds = [];

    integrations.forEach((integration) => {
      byId[integration.id] = integration;
      allIds.push(integration.id);
    });

    return state
      .merge({
        byId,
        allIds,
      })
      .set('isFetching', false);
  });

  on(IntegrationActions.fetchIntegrationCredentials, (state) => state.set('isFetchingCredentials', true));

  on(IntegrationActions.fetchIntegrationCredentialsFailure, (state) => state.set('isFetchingCredentials', false));

  on(IntegrationActions.fetchIntegrationCredentialsSuccess, (state, { credentials }) =>
    state.set('credentialsById', _.keyBy(credentials, 'id')).set('isFetchingCredentials', false)
  );

  on(IntegrationActions.saveIntegrationCredentialsSuccess, (state, { credentials }) =>
    state.setIn(['credentialsById', credentials.id], credentials)
  );

  on(IntegrationActions.deleteIntegrationCredentialsSuccess, (state, { credentialsId }) =>
    state.set('credentialsById', state.credentialsById.without(credentialsId))
  );

  on(
    [
      IntegrationActions.updateIntegrationSuccess,
      IntegrationActions.updateAndActivateIntegrationSuccess,
      IntegrationActions.setIntegration,
    ],
    (state, { integration }) => state.setIn(['byId', integration.id], integration)
  );

  on(IntegrationActions.createFromTemplateSuccess, (state, { integration }) => {
    const _updatedState = state;
    const _allIds = [integration.id].concat(_.without(_updatedState.allIds, integration.id));

    return _updatedState.setIn(['byId', integration.id], integration).set('allIds', _allIds);
  });

  on(IntegrationActions.deleteIntegrationSuccess, (state, { integrationId }) => {
    const _updatedState = state;
    const _byId = _updatedState.byId.without(integrationId);
    const _allIds = state.allIds.filter((id) => id !== integrationId);

    return state.set('byId', _byId).set('allIds', _allIds);
  });

  on(IntegrationActions.enableIntegration, (state, { integrationId }) => {
    return state.setIn(
      ['byId', integrationId, 'status'],
      RefData.Model.hydrate(RefData.Enum.STATUS.INTEGRATION.ACTIVE)
    );
  });

  on(IntegrationActions.disableIntegration, (state, { integrationId }) => {
    return state.setIn(
      ['byId', integrationId, 'status'],
      RefData.Model.hydrate(RefData.Enum.STATUS.INTEGRATION.INACTIVE)
    );
  });

  on(IntegrationActions.enableIntegrationFailure, (state, { integrationId }) => {
    return state.setIn(
      ['byId', integrationId, 'status'],
      RefData.Model.hydrate(RefData.Enum.STATUS.INTEGRATION.INACTIVE)
    );
  });

  on(IntegrationActions.disableIntegrationFailure, (state, { integrationId }) => {
    return state.setIn(
      ['byId', integrationId, 'status'],
      RefData.Model.hydrate(RefData.Enum.STATUS.INTEGRATION.ACTIVE)
    );
  });

  on(IntegrationActions.updateIntegrationFieldById, (state, { integrationId, fieldId, value }) =>
    state.setIn(['byId', integrationId, fieldId], value)
  );

  // ################################################################################################################
  // WIZARD INTEGRATION REDUCERS
  // ################################################################################################################
  on(IntegrationActions.openIntegrationWizard, (state, { wizardTemplate }) =>
    state.setIn(['wizardIntegration', 'isActive'], true).setIn(['wizardIntegration', 'template'], wizardTemplate)
  );
  on(IntegrationActions.clearWizardData, (state) => state.set('wizardIntegration', initialWizardIntegrationState));
  on(IntegrationActions.setActiveIntegrationTemplate, (state, { wizardTemplate }) =>
    state.setIn(['wizardIntegration', 'template'], wizardTemplate)
  );
  on(IntegrationActions.startWizardLoading, (state) => state.setIn(['wizardIntegration', 'isLoading'], true));
  on(IntegrationActions.addWizardCredentialsData, (state, { credentials }) =>
    state
      .setIn(['wizardIntegration', 'dataSources', 'credentialsId', 'content'], credentials)
      .setIn(['wizardIntegration', 'isLoading'], false)
  );
  on(IntegrationActions.addWizardApiDefinition, (state, { apiUrl }) =>
    state.setIn(['wizardIntegration', 'apiUrl'], apiUrl)
  );
  on(IntegrationActions.addWizardCampaignId, (state, { id }) => state.setIn(['wizardIntegration', 'campaignId'], id));
  on(IntegrationActions.addWizardSteps, (state, { steps }) =>
    state.setIn(['wizardIntegration', 'steps'], [...state.wizardIntegration.steps, ...steps])
  );
  on(IntegrationActions.addWizardFields, (state, { blueprintData }) => {
    const wizardSteps = state.getIn(['wizardIntegration', 'steps']);
    const existentWizardFields = state.getIn(['wizardIntegration', 'fields']);
    return state.setIn(
      ['wizardIntegration', 'fields'],
      prepareWizardFields(wizardSteps, blueprintData, existentWizardFields)
    );
  });
  on(IntegrationActions.setInitialAPISetup, (state, { isInitial }) =>
    state.setIn(['wizardIntegration', 'initialAPISetup'], isInitial)
  );
  on(IntegrationActions.updateIntegrationWizardField, (state, { fieldId, value }) =>
    state.setIn(['wizardIntegration', 'fields', fieldId], value)
  );
  on(IntegrationActions.addWizardMapping, (state, { blueprintData }) =>
    state.setIn(['wizardIntegration', 'mapping'], blueprintData.mapping)
  );
  on(IntegrationActions.updateWizardMappingField, (state, { idx, value, fieldName }) =>
    state.setIn(['wizardIntegration', 'mapping', idx, fieldName], value)
  );
  on(IntegrationActions.updateWizardMappingRequiredField, (state, { idx }) =>
    state.setIn(['wizardIntegration', 'mapping', idx, 'required'], true)
  );
  on(IntegrationActions.addWizardDefaultMapping, (state, { defaultFields, hasMandatoryRow }) => {
    let defaultColumns = [];
    if (defaultFields.length) {
      defaultColumns = defaultFields.map((field) => ({
        from:     field,
        to:       '',
        required: false,
      }));
    } else if (hasMandatoryRow) {
      // if there are no default fields, but configuration requires to have at least one mandatory row, we add a mandatory empty one
      defaultColumns = [{ from: '', to: '', required: false, mandatoryRow: true }];
    }
    return state.setIn(['wizardIntegration', 'mapping'], defaultColumns);
  });
  on(
    IntegrationActions.addWizardAPIMapping,
    (state, { mandatoryFields, defaultFields, isInitialAPISetup, isTypeahead }) => {
      const mappingColumns = state.getIn(['wizardIntegration', 'mapping']);
      const mandatoryColumns = mandatoryFields.map((field) => ({
        from:         field.mandatory,
        to:           field.id,
        required:     !isTypeahead,
        primaryKey:   field.primaryKey,
        nonDeletable: field.nonDeletable,
        type:         field.type,
      }));

      const defaultColumns = defaultFields.map((field) => ({
        from:     field.id,
        to:       field.id,
        type:     field.type,
        required: true,
      }));
      // need to merge inner and API default columns. API default columns means to be required, so they will rewrite the inner ones
      let mergedDefaultMappingColumns = [];
      if (isInitialAPISetup) {
        const requiredColumns = [...mandatoryColumns, ...defaultColumns];
        mergedDefaultMappingColumns = _.unionBy(
          requiredColumns,
          mappingColumns,
          IntegrationEnums.integrationWizardMappingDirections.FROM
        );
      } else {
        mergedDefaultMappingColumns = mappingColumns;
      }
      return state.setIn(['wizardIntegration', 'mapping'], mergedDefaultMappingColumns);
    }
  );
  on(IntegrationActions.addMappingRow, (state) => {
    const mappingColumns = state.getIn(['wizardIntegration', 'mapping']);
    return state.setIn(['wizardIntegration', 'mapping'], mappingColumns.concat({ from: '', to: '' }));
  });
  on(IntegrationActions.deleteMappingRow, (state, { idx }) => {
    const mappingColumns = state.getIn(['wizardIntegration', 'mapping']);
    return state.setIn(
      ['wizardIntegration', 'mapping'],
      mappingColumns.filter((value, key) => key !== idx)
    );
  });
  on(IntegrationActions.fetchDataSource, (state, { fieldId }) =>
    state.setIn(['wizardIntegration', 'dataSources', fieldId, 'isFetching'], true)
  );
  on(IntegrationActions.fetchDataSourceSuccess, (state, { fieldId, data }) =>
    state
      .setIn(['wizardIntegration', 'dataSources', fieldId, 'content'], data)
      .setIn(['wizardIntegration', 'dataSources', fieldId, 'isFetching'], false)
  );
  on(IntegrationActions.fetchDataSourceFailure, (state, { fieldId }) =>
    state
      .setIn(['wizardIntegration', 'dataSources', fieldId, 'content'], [])
      .setIn(['wizardIntegration', 'dataSources', fieldId, 'isFetching'], false)
  );

  on(IntegrationActions.fetchMappingData, (state) =>
    state.setIn(['wizardIntegration', 'mappingData', 'isFetching'], true)
  );
  on(IntegrationActions.fetchMappingDataSuccess, (state, { data }) =>
    state
      .setIn(['wizardIntegration', 'mappingData', 'content'], data)
      .setIn(['wizardIntegration', 'mappingData', 'isFetching'], false)
  );
  on(IntegrationActions.fetchMappingDataFailure, (state) =>
    state
      .setIn(['wizardIntegration', 'mappingData', 'content'], [])
      .setIn(['wizardIntegration', 'mappingData', 'isFetching'], false)
  );
  on(IntegrationActions.toggleAllowSwitchToNextStep, (state, { allowNextStep }) =>
    state.setIn(['wizardIntegration', 'allowNextStep'], allowNextStep)
  );
}, initialState);
